import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError, ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import { itemStatuses, type Item, type ItemPatch, type Site } from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { sortByDisplayLabel } from '../../lib/formatting';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';
import { formatDiffValue } from '../item-detail/audit-diff';
import { MOVED_ACTIVE_HINT, MOVED_ACTIVE_STATUS, statusForMove } from '../inventory/active-move';
import {
  buildItemEditPatch,
  initialItemEditDraft,
  itemEditErrorMessage,
  type ItemEditDraft,
  type ItemEditMode,
} from './edit-item-draft';

const EDITABLE_FIELD_LABELS: Record<keyof ItemEditDraft, string> = {
  status: 'Status',
  needsAttention: 'Needs attention',
  attentionReason: 'Attention reason',
  hardwareRevision: 'Hardware revision',
  replacedByItemId: 'Replaced by',
  notes: 'Notes',
  location: 'Location detail',
  locationSiteId: 'Current location site',
  siteId: 'Assigned site',
  inspectedDate: 'Inspected date',
  inspectedBy: 'Inspected by',
  maintenanceDate: 'Maintained date',
  maintenanceBy: 'Maintained by',
  maintenanceNotes: 'Maintenance notes',
};
const alphabetizedStatuses = sortByDisplayLabel(itemStatuses, (status) => statusLabels[status]);

function toDateInputValue(value: string | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function fromDateInputValue(value: string): string | undefined {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined;
}

interface EditItemDrawerProps {
  item: Item;
  sites: Site[];
  mode?: ItemEditMode;
  onClose: () => void;
}

export function EditItemDrawer({ item, sites, mode = 'edit', onClose }: EditItemDrawerProps) {
  const queryClient = useQueryClient();
  const sortedSites = useMemo(
    () => sortByDisplayLabel(sites, (site) => siteDisplayName(site)),
    [sites],
  );
  const [draft, setDraft] = useState<ItemEditDraft>(() => initialItemEditDraft(mode));
  const [statusChosen, setStatusChosen] = useState(false);
  const [loadedVersion, setLoadedVersion] = useState(item.version);
  const [conflict, setConflict] = useState<{ latest: Item } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const current: Item & ItemEditDraft = { ...item, version: loadedVersion, ...draft };

  const mutation = useMutation({
    mutationFn: (patch: ItemPatch) => inventoryApi.updateItem(patch),
    onSuccess: async () => {
      // Refetch the item, its history, and the visible Inventory list right
      // away. The AppShell/Dashboard/Sites/Parts full-fleet query is only
      // marked stale (refetchType: 'none') rather than eagerly refetched —
      // otherwise every single save would re-pull the entire ~2.5MB item
      // list synchronously. It refreshes lazily next time it's actually
      // viewed (new observer mount) or the window regains focus.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['item', item.id] }),
        queryClient.invalidateQueries({ queryKey: ['item-history', item.id] }),
        queryClient.invalidateQueries({ queryKey: ['items'] }),
      ]);
      void queryClient.invalidateQueries({
        queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
        refetchType: 'none',
      });
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ConflictError) {
        inventoryApi
          .getItem(item.id)
          .then((latest) => setConflict({ latest }))
          .catch(() =>
            setFormError(
              'The item changed after it was loaded, and the latest version could not be fetched.',
            ),
          );
        return;
      }
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(itemEditErrorMessage(error));
    },
  });

  function submit(version: number, patch: ItemEditDraft): void {
    setFormError(null);
    setUnknownOutcome(false);
    mutation.mutate(buildItemEditPatch(item, version, patch));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submit(loadedVersion, draft);
  }

  function updateDraft<K extends keyof ItemEditDraft>(key: K, value: ItemEditDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  // Moving an Active item pre-selects Delivered to customer, unless a status
  // was picked by hand; moving it back undoes that.
  function updateSiteDraft(key: 'siteId' | 'locationSiteId', value: string | undefined): void {
    setDraft((prev) => {
      const next: ItemEditDraft = { ...prev, [key]: value };
      if (statusChosen) return next;
      const moved = statusForMove(item, next);
      if (moved) return { ...next, status: moved };
      const rest = { ...next };
      delete rest.status;
      return rest;
    });
  }

  if (conflict) {
    const changedFields = Object.keys(draft) as (keyof ItemEditDraft)[];
    return (
      <div className="drawer-overlay" role="presentation" onClick={onClose}>
        <div
          className="drawer conflict-drawer"
          role="dialog"
          aria-label="Version conflict"
          onClick={(event) => event.stopPropagation()}
        >
          <h2>{item.id} changed while you were editing</h2>
          <p className="conflict-summary">
            Loaded version {loadedVersion} · current server version {conflict.latest.version}
          </p>
          {changedFields.length > 0 ? (
            <table className="conflict-table">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Server</th>
                  <th scope="col">Your draft</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map((field) => (
                  <tr key={field}>
                    <td>{EDITABLE_FIELD_LABELS[field]}</td>
                    <td>{formatDiffValue(conflict.latest[field])}</td>
                    <td className="conflict-draft-cell">{formatDiffValue(draft[field])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="conflict-actions">
            <button type="button" onClick={onClose}>
              Discard my draft
            </button>
            <button
              type="button"
              onClick={() => {
                setLoadedVersion(conflict.latest.version);
                setDraft({});
                setConflict(null);
              }}
            >
              Keep server values
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                const nextVersion = conflict.latest.version;
                setLoadedVersion(nextVersion);
                setConflict(null);
                submit(nextVersion, draft);
              }}
            >
              Reapply onto v{conflict.latest.version}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer"
        aria-label={`Edit ${item.id}`}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2>
          {mode === 'clear-attention' ? `Clear attention flag for ${item.id}` : `Edit ${item.id}`}
        </h2>
        <p className="drawer-version-note">Editing from version {loadedVersion}.</p>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['item', item.id] });
              onClose();
            }}
          />
        ) : null}

        <label>
          Status
          <SearchableSelect
            ariaLabel="Status"
            onChange={(value) => {
              setStatusChosen(true);
              updateDraft('status', (value || undefined) as ItemEditDraft['status']);
            }}
            options={[
              { value: '', label: `Unchanged (${current.status ?? 'STATUS_UNSPECIFIED'})` },
              ...alphabetizedStatuses.map((value) => ({
                value,
                label: statusLabels[value],
                aliases: [value],
              })),
            ]}
            placeholder="Select a status"
            value={current.status ?? ''}
          />
          {!statusChosen && item.status === 'ACTIVE' && draft.status === MOVED_ACTIVE_STATUS ? (
            <span className="field-hint">{MOVED_ACTIVE_HINT}</span>
          ) : null}
        </label>
        {current.status === 'REPLACED' ? (
          <label>
            Replaced by
            <input
              type="text"
              className="mono"
              value={current.replacedByItemId ?? ''}
              onChange={(event) => updateDraft('replacedByItemId', event.target.value.trim())}
              placeholder="Item ID of the device that took its pen"
              required
            />
            <span className="field-hint">
              Out of service and awaiting return; not counted as active or billable.
            </span>
          </label>
        ) : null}

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={current.needsAttention ?? false}
            onChange={(event) => {
              updateDraft('needsAttention', event.target.checked);
              if (!event.target.checked) updateDraft('attentionReason', '');
            }}
          />
          Needs attention
        </label>
        {current.needsAttention ? (
          <label>
            Attention reason
            <input
              type="text"
              value={current.attentionReason ?? ''}
              onChange={(event) => updateDraft('attentionReason', event.target.value)}
              required
            />
          </label>
        ) : null}

        <label>
          Current location site
          <SearchableSelect
            ariaLabel="Current location site"
            onChange={(value) => updateSiteDraft('locationSiteId', value || undefined)}
            options={[
              { value: '', label: 'Unchanged' },
              ...sortedSites.map((site) => ({
                value: site.id,
                label: siteLabel(site),
                aliases: siteSearchAliases(site),
              })),
            ]}
            placeholder="Select a location site"
            value={current.locationSiteId ?? ''}
          />
        </label>
        <label>
          Assigned site
          <SearchableSelect
            ariaLabel="Assigned site"
            onChange={(value) => updateSiteDraft('siteId', value || undefined)}
            options={[
              { value: '', label: 'Unchanged' },
              ...sortedSites.map((site) => ({
                value: site.id,
                label: siteLabel(site),
                aliases: siteSearchAliases(site),
              })),
            ]}
            placeholder="Select an assigned site"
            value={current.siteId ?? ''}
          />
        </label>
        <label>
          Location detail
          <input
            type="text"
            value={current.location ?? ''}
            onChange={(event) => updateDraft('location', event.target.value)}
          />
        </label>

        <label>
          Hardware revision
          <input
            type="text"
            value={current.hardwareRevision ?? ''}
            onChange={(event) => updateDraft('hardwareRevision', event.target.value)}
          />
        </label>

        <label>
          Notes
          <textarea
            value={current.notes ?? ''}
            onChange={(event) => updateDraft('notes', event.target.value)}
            rows={3}
          />
        </label>

        <div className="form-row">
          <label>
            Inspected date
            <input
              type="date"
              value={toDateInputValue(current.inspectedDate)}
              onChange={(event) =>
                updateDraft('inspectedDate', fromDateInputValue(event.target.value))
              }
            />
          </label>
          <label>
            Inspected by
            <input
              type="text"
              value={current.inspectedBy ?? ''}
              onChange={(event) => updateDraft('inspectedBy', event.target.value)}
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            Maintained date
            <input
              type="date"
              value={toDateInputValue(current.maintenanceDate)}
              onChange={(event) =>
                updateDraft('maintenanceDate', fromDateInputValue(event.target.value))
              }
            />
          </label>
          <label>
            Maintained by
            <input
              type="text"
              value={current.maintenanceBy ?? ''}
              onChange={(event) => updateDraft('maintenanceBy', event.target.value)}
            />
          </label>
        </div>
        <label>
          Maintenance notes
          <textarea
            value={current.maintenanceNotes ?? ''}
            onChange={(event) => updateDraft('maintenanceNotes', event.target.value)}
            rows={2}
          />
        </label>

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary"
            disabled={mutation.isPending || Object.keys(draft).length === 0}
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
